import React from 'react';
import KeystoneLogo from '../components/KeystoneLogo';
import { downloadServerPDF } from '../lib/pdf-download';

// Investor one-pager: a single A4 teaser. Belief -> gap -> traction ->
// model -> contact. Server-rendered to PDF via /api/marketing/render-pdf.

const C = {
  navy: '#0A2540', navyDeep: '#061A2E', navyMid: '#163E66',
  magenta: '#E91E63', gold: '#C9A961', cream: '#FAF8F5',
  ink: '#0F1B2A', slate: '#3D4A5C', muted: '#6B7280', border: '#E2E8F0',
};

const Label = ({ children, color = C.gold }) => (
  <div style={{ fontSize: '7.5pt', letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700, color, marginBottom: '2.5mm' }}>{children}</div>
);

export default function BrochureInvestor() {
  const [downloading, setDownloading] = React.useState(false);
  const [error, setError] = React.useState('');

  const onDownload = async () => {
    setError('');
    setDownloading(true);
    try {
      await downloadServerPDF('/brochure/investor', 'Keystone-Investor-One-Pager.pdf');
    } catch (e) {
      setError(e.message || 'PDF render failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="sales-brochure-stack" style={{ background: '#EDEEF0', minHeight: '100vh', padding: '32px 0', fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
      <div className="brochure-controls" data-testid="investor-pager-controls" style={{
        position: 'sticky', top: 12, zIndex: 20, maxWidth: '210mm', margin: '0 auto 20px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 14px', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 6,
        boxShadow: '0 4px 20px rgba(10,37,64,0.10)',
      }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.navy }}>Keystone Investor One-Pager</div>
          <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>1 page · A4 · server-rendered PDF</div>
        </div>
        <button data-testid="investor-pager-download" onClick={onDownload} disabled={downloading} style={{
          padding: '9px 18px', background: C.navy, color: '#fff', border: 'none', borderRadius: 4,
          fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
          cursor: downloading ? 'wait' : 'pointer', opacity: downloading ? 0.7 : 1,
        }}>
          {downloading ? 'Rendering…' : 'Download PDF'}
        </button>
      </div>
      {error && <div style={{ maxWidth: '210mm', margin: '0 auto 16px', padding: '10px 14px', background: '#FEE2E2', color: '#991B1B', borderRadius: 4, fontSize: 12 }}>{error}</div>}

      <section className="brochure-page" data-page="1" style={{
        width: '210mm', height: '297mm', background: C.navy, position: 'relative', overflow: 'hidden',
        margin: '0 auto', boxSizing: 'border-box', color: '#fff', boxShadow: '0 8px 32px rgba(10,37,64,0.12)',
      }}>
        <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(circle at 15% 10%, ${C.navyMid} 0%, ${C.navy} 45%, ${C.navyDeep} 100%)` }} />
        <div style={{ position: 'relative', height: '100%', padding: '12mm 14mm', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <KeystoneLogo height={34} variant="dark" showTagline={false} />
            <div style={{ textAlign: 'right', fontSize: '7.5pt', color: 'rgba(255,255,255,0.6)', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700 }}>
              Investor teaser · 2026<br/>
              <span style={{ color: C.gold }}>EU + UK food manufacturing</span>
            </div>
          </div>

          {/* Belief */}
          <div style={{ marginTop: '8mm' }}>
            <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '25pt', fontWeight: 700, lineHeight: 1.06, letterSpacing: '-0.02em', margin: 0 }}>
              You shouldn’t need an enterprise ERP<br/>
              <em style={{ fontStyle: 'italic', fontWeight: 500, color: C.gold }}>to prove your food is safe.</em>
            </h1>
            <p style={{ fontSize: '9.5pt', lineHeight: 1.5, color: 'rgba(255,255,255,0.8)', marginTop: '4mm', maxWidth: '165mm', fontWeight: 300 }}>
              Keystone is the operations layer for small and mid-size food manufacturers: batch traceability, production, dispatch, retailer EDI and audit evidence in one system. Written on the production floor of an Irish farmhouse cheesemaker, not in a product workshop, and sold with published pricing and no sales funnel.
            </p>
          </div>

          {/* The gap */}
          <div style={{ marginTop: '6mm' }}>
            <Label>The gap</Label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '3mm' }}>
              {[
                ['The market nobody builds for', 'Europe\u2019s food and drink industry is overwhelmingly small producers with real traceability obligations. They sit between Excel and \u20AC40k+ ERP projects, and stay on Excel.'],
                ['The regulatory clock', 'EU Regulation 178/2002 makes traceability law. BRCGS Issue 9 turns it into a timed 4-hour test. Retailers audit it, and failure costs the listing.'],
                ['The incumbent blind spot', 'Enterprise vendors (CSB, Aptean, DIOMAC) quote 6-24 month projects. Horizontal SMB tools can\u2019t hold catch-weight, lot genealogy or retailer SKU mapping.'],
              ].map(([t, b]) => (
                <div key={t} style={{ padding: '3.5mm 4mm', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderTop: `2px solid ${C.magenta}` }}>
                  <div style={{ fontSize: '9pt', fontWeight: 700, color: '#fff', marginBottom: 3, lineHeight: 1.3 }}>{t}</div>
                  <div style={{ fontSize: '7.8pt', color: 'rgba(255,255,255,0.72)', lineHeight: 1.5 }}>{b}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Traction */}
          <div style={{ marginTop: '5mm' }}>
            <Label>Traction, all verifiable</Label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '3mm' }}>
              {[
                ['2h → 30s', 'measured trace-time reduction at our founding partner, Cashel Blue'],
                ['40+ SKUs', '9 export markets and 400+ retailer SKU mappings run in Keystone daily'],
                ['30 days', 'from contract to live production, founder-led implementation'],
                ['4 languages', 'localized product + 60+ SEO landing pages across EN, HU, PL, DE'],
              ].map(([k, v]) => (
                <div key={k} style={{ padding: '3.5mm 3mm', background: C.cream, textAlign: 'center' }}>
                  <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '17pt', fontWeight: 700, color: C.magenta, lineHeight: 1 }}>{k}</div>
                  <div style={{ fontSize: '7.3pt', color: C.slate, marginTop: 3, lineHeight: 1.4 }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '2.5mm', fontSize: '7.8pt', color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>
              Founding partner live in production since 2024: Cashel Farmhouse Cheesemakers (Cashel Blue), three generations, exports to nine markets, private-label lines with Tesco Finest and M&S. Founder’s Club early-adopter cohort now open across the EU and UK.
            </div>
          </div>

          {/* Model + moat */}
          <div style={{ marginTop: '5mm', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3mm' }}>
            <div style={{ padding: '4mm', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}>
              <Label>Business model</Label>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: '8pt', color: 'rgba(255,255,255,0.78)', lineHeight: 1.55 }}>
                <li>• SaaS licence per site, every module included, published pricing (from €790/month)</li>
                <li>• Fixed-fee implementation quoted in writing before signature, no billable-hours drift</li>
                <li>• Land on traceability pain, expand into planning, purchasing and multi-site capacity</li>
              </ul>
            </div>
            <div style={{ padding: '4mm', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}>
              <Label>Why we win</Label>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: '8pt', color: 'rgba(255,255,255,0.78)', lineHeight: 1.55 }}>
                <li>• Built beside the customer: features come from factory floors, shipped in days</li>
                <li>• A trust brand in a cynical category: honest comparisons, refundable pilot, no hostage data</li>
                <li>• 15+ years enterprise delivery discipline (Dynamics for Kaspersky, SAP) at artisan price points</li>
              </ul>
            </div>
          </div>

          <div style={{ flex: 1 }} />

          {/* Verifiable proof, not a fabricated pull-quote */}
          <div style={{ padding: '4mm 5mm', background: 'rgba(201,169,97,0.10)', borderLeft: `2px solid ${C.gold}` }}>
            <div style={{ fontSize: '9pt', color: 'rgba(255,255,255,0.9)', lineHeight: 1.5 }}>
              Reference call available with <strong style={{ color: C.gold }}>Cashel Farmhouse Cheesemakers</strong> (multi-award-winning Irish blue-cheese producer, international export footprint, live on Keystone since Feb 2026). Ask on the intro call and we&rsquo;ll broker it.
            </div>
          </div>

          {/* Contact */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '3.5mm', marginTop: '4mm', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div>
              <div style={{ fontSize: '9.5pt', fontWeight: 700, color: '#fff' }}>Asif Khan, founder</div>
              <div style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
                CloudVoro (Ireland) · sister company to Intellix IT Solutions (UK)
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: '8.5pt', lineHeight: 1.5, color: 'rgba(255,255,255,0.8)' }}>
              +353 85 181 0310 · hello@cloudvoro.com<br/>
              <span style={{ color: C.gold }}>keystone.cloudvoro.com</span>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @page { size: A4; margin: 0; }
        body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        @media screen and (max-width: 820px) {
          html, body { overflow-x: hidden; }
          .brochure-page { zoom: calc((100vw - 16px) / 210mm); }
          .brochure-controls { max-width: calc(100vw - 20px) !important; }
        }
        @media print {
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          .brochure-controls { display: none !important; }
          .sales-brochure-stack { padding: 0 !important; }
          .brochure-page { box-shadow: none !important; margin: 0 !important; }
        }
      `}</style>
    </div>
  );
}
